import { useState, useRef } from 'react';
import {
  useBirthdayEmployees,
  useBirthdayTicketLogs,
  useUploadBirthdays,
  useProcessTodaysBirthdays,
  useResendBirthdayEmail,
  useUpdateBirthdayEmail,
  useDeactivateBirthdayEmployee,
  type BirthdayEmployee,
  type UploadSummary,
  type ProcessSummary,
} from '../../hooks/useBirthdays';

type Tab = 'employees' | 'logs' | 'upload';

const MONTHS = [
  'All Months', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function BirthdaysPage() {
  const [activeTab, setActiveTab] = useState<Tab>('employees');

  const tabs: { key: Tab; label: string }[] = [
    { key: 'employees', label: 'Employees' },
    { key: 'logs', label: 'Logs' },
    { key: 'upload', label: 'Upload' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Birthday Meal Tickets</h1>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === tab.key
                ? 'text-white'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
            style={activeTab === tab.key ? { backgroundColor: '#10348e' } : undefined}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'employees' && <EmployeesTab />}
      {activeTab === 'logs' && <LogsTab />}
      {activeTab === 'upload' && <UploadTab />}
    </div>
  );
}

// =============================================================================
// Employees Tab
// =============================================================================

function EmployeesTab() {
  const [monthFilter, setMonthFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState('');

  const { data: employees, isLoading, error } = useBirthdayEmployees({
    is_active: true,
    month: monthFilter,
    search: search || undefined,
  });
  const updateEmailMutation = useUpdateBirthdayEmail();
  const deactivateMutation = useDeactivateBirthdayEmployee();

  const withEmail = employees?.filter(e => e.email)?.length ?? 0;
  const withoutEmail = employees?.filter(e => !e.email)?.length ?? 0;

  const handleSaveEmail = async (id: number) => {
    if (!editEmail.trim()) return;
    await updateEmailMutation.mutateAsync({ id, email: editEmail.trim() });
    setEditingId(null);
    setEditEmail('');
  };

  const startEditEmail = (emp: BirthdayEmployee) => {
    setEditingId(emp.id);
    setEditEmail(emp.email || '');
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4 items-center">
          <select
            value={monthFilter ?? 0}
            onChange={(e) => setMonthFilter(Number(e.target.value) || undefined)}
            className="border rounded px-3 py-2 text-sm"
          >
            {MONTHS.map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-64"
          />
        </div>
        <div className="text-sm text-gray-500">
          <span className="text-green-600 font-medium">{withEmail}</span> with email,{' '}
          <span className="text-amber-600 font-medium">{withoutEmail}</span> without
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : error ? (
        <div className="p-8 text-center text-red-600">Error: {(error as Error).message}</div>
      ) : !employees || employees.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No birthday employees found. Upload a spreadsheet to get started.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#10348e', color: 'white' }}>
                <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Birthday</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employees.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{emp.employee_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {MONTHS[emp.birthday_month]} {emp.birthday_day}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {editingId === emp.id ? (
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          className="border rounded px-2 py-1 text-sm w-48"
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveEmail(emp.id)}
                        />
                        <button
                          onClick={() => handleSaveEmail(emp.id)}
                          disabled={updateEmailMutation.isPending}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className={emp.email ? 'text-gray-600' : 'text-amber-500 italic'}>
                        {emp.email || 'No email'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      emp.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <div className="flex gap-2 justify-end">
                      {editingId !== emp.id && (
                        <button
                          onClick={() => startEditEmail(emp)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit Email
                        </button>
                      )}
                      <button
                        onClick={() => deactivateMutation.mutate(emp.id)}
                        disabled={deactivateMutation.isPending}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Logs Tab
// =============================================================================

function LogsTab() {
  const [dateFilter, setDateFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState<boolean | undefined>(undefined);

  const { data: logs, isLoading, error } = useBirthdayTicketLogs({
    birthday_date: dateFilter || undefined,
    email_sent: emailFilter,
  });
  const resendMutation = useResendBirthdayEmail();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex gap-4 items-center">
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        />
        <select
          value={emailFilter === undefined ? '' : String(emailFilter)}
          onChange={(e) => setEmailFilter(e.target.value === '' ? undefined : e.target.value === 'true')}
          className="border rounded px-3 py-2 text-sm"
        >
          <option value="">All Email Status</option>
          <option value="true">Email Sent</option>
          <option value="false">Email Not Sent</option>
        </select>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : error ? (
        <div className="p-8 text-center text-red-600">Error: {(error as Error).message}</div>
      ) : !logs || logs.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No ticket logs found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: '#10348e', color: 'white' }}>
                <th className="px-4 py-3 text-left text-sm font-semibold">Employee</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Ticket Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Birthday</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Error</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.employee_name}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{log.ticket_code || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{log.birthday_date}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      log.email_sent ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {log.email_sent ? 'Sent' : 'Not Sent'}
                    </span>
                    {log.email_sent_at && (
                      <span className="ml-2 text-xs text-gray-400">
                        {new Date(log.email_sent_at).toLocaleString()}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-red-600 max-w-xs truncate" title={log.email_error || undefined}>
                    {log.email_error || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {!log.email_sent && (
                      <button
                        onClick={() => resendMutation.mutate(log.id)}
                        disabled={resendMutation.isPending}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {resendMutation.isPending ? 'Sending...' : 'Resend'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Upload Tab
// =============================================================================

function UploadTab() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadResult, setUploadResult] = useState<UploadSummary | null>(null);
  const [processResult, setProcessResult] = useState<ProcessSummary | null>(null);
  const [confirmProcess, setConfirmProcess] = useState(false);

  const uploadMutation = useUploadBirthdays();
  const processMutation = useProcessTodaysBirthdays();

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;

    try {
      const result = await uploadMutation.mutateAsync(file);
      setUploadResult(result);
      if (fileRef.current) fileRef.current.value = '';
    } catch {
      // error handled by mutation state
    }
  };

  const handleProcess = async () => {
    try {
      const result = await processMutation.mutateAsync();
      setProcessResult(result);
      setConfirmProcess(false);
    } catch {
      setConfirmProcess(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Birthday Spreadsheet</h2>
        <p className="text-sm text-gray-600 mb-4">
          Upload a .xlsx or .csv file with two columns: <strong>Name</strong> and <strong>Birthday</strong> (MM/DD format).
          Existing employees will be updated; new ones will be added.
        </p>
        <div className="flex gap-4 items-center">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv"
            className="border rounded px-3 py-2 text-sm"
          />
          <button
            onClick={handleUpload}
            disabled={uploadMutation.isPending}
            className="px-4 py-2 text-white rounded font-medium transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#10348e' }}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </button>
        </div>

        {uploadMutation.isError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {(uploadMutation.error as Error).message}
          </div>
        )}

        {uploadResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
            <h3 className="font-medium text-green-800 mb-2">Upload Complete</h3>
            <div className="text-sm text-green-700 space-y-1">
              <p>Total records: <strong>{uploadResult.total}</strong></p>
              <p>Added: <strong>{uploadResult.added}</strong></p>
              <p>Updated: <strong>{uploadResult.updated}</strong></p>
              {uploadResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-amber-700 font-medium">Errors ({uploadResult.errors.length}):</p>
                  <ul className="list-disc list-inside text-amber-600">
                    {uploadResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Process Today Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Process Today's Birthdays</h2>
        <p className="text-sm text-gray-600 mb-4">
          Manually trigger birthday processing for today. This will create meal tickets and send emails
          to employees whose birthday is today. The scheduler also runs this automatically each morning.
        </p>

        {!confirmProcess ? (
          <button
            onClick={() => setConfirmProcess(true)}
            className="px-4 py-2 bg-amber-500 text-white rounded font-medium hover:bg-amber-600 transition-colors"
          >
            Process Today
          </button>
        ) : (
          <div className="flex gap-3 items-center">
            <span className="text-sm text-amber-700 font-medium">Are you sure?</span>
            <button
              onClick={handleProcess}
              disabled={processMutation.isPending}
              className="px-4 py-2 bg-amber-500 text-white rounded font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
            >
              {processMutation.isPending ? 'Processing...' : 'Yes, Process'}
            </button>
            <button
              onClick={() => setConfirmProcess(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded font-medium hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {processMutation.isError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {(processMutation.error as Error).message}
          </div>
        )}

        {processResult && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="font-medium text-blue-800 mb-2">Processing Complete</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p>Date: <strong>{processResult.date}</strong></p>
              <p>Tickets created: <strong>{processResult.processed}</strong></p>
              <p>Already processed (skipped): <strong>{processResult.skipped}</strong></p>
              <p>Emails sent: <strong>{processResult.email_sent}</strong></p>
              <p>Email failures: <strong>{processResult.email_failed}</strong></p>
              {processResult.results.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Results:</p>
                  <ul className="list-disc list-inside">
                    {processResult.results.map((r, i) => (
                      <li key={i}>
                        {r.employee} — {r.ticket_code} {r.has_email ? '(email queued)' : '(no email)'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
